import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Search, Plus, Users, Briefcase, DollarSign, Phone, Mail, ChevronLeft, ChevronRight, Calendar, Clock, CheckCircle, XCircle, TrendingUp, Edit, ChevronDown, PieChart } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../services/api';
import { useData } from '../contexts/DataContext';
import BatchUploadModal from '../components/BatchUploadModal';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import InfoTooltip from '../components/ui/InfoTooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const DOCUMENT_TYPES = ['NIT', 'CC', 'CE'];
const PERSON_TYPES = ['Persona Natural', 'Persona Jurídica'];

// Country codes for phone numbers
const COUNTRY_CODES = [
  { code: '+57', country: 'Colombia', flag: '🇨🇴' },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸' },
  { code: '+52', country: 'México', flag: '🇲🇽' },
  { code: '+34', country: 'España', flag: '🇪🇸' },
  { code: '+54', country: 'Argentina', flag: '🇦🇷' },
  { code: '+56', country: 'Chile', flag: '🇨🇱' },
  { code: '+51', country: 'Perú', flag: '🇵🇪' },
  { code: '+593', country: 'Ecuador', flag: '🇪🇨' },
  { code: '+58', country: 'Venezuela', flag: '🇻🇪' },
  { code: '+55', country: 'Brasil', flag: '🇧🇷' },
  { code: '+507', country: 'Panamá', flag: '🇵🇦' },
];

const PERIOD_OPTIONS = [
  { value: 'week', label: 'Esta Semana' },
  { value: 'month', label: 'Este Mes' },
  { value: 'quarter', label: 'Este Trimestre' },
  { value: 'semester', label: 'Este Semestre' },
  { value: 'year', label: 'Este Año' },
  { value: 'all', label: 'Todo el Tiempo' },
  { value: 'custom', label: 'Rango Personalizado' },
];

export default function Empresas() {
  const navigate = useNavigate();
  const { state } = useData();
  const [activeTab, setActiveTab] = useState('empresas');
  
  // Period filter state
  const [period, setPeriod] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  
  // Empresas state
  const [empresas, setEmpresas] = useState([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(true);
  const [searchEmpresa, setSearchEmpresa] = useState('');
  const [pageEmpresa, setPageEmpresa] = useState(1);
  const [totalPagesEmpresa, setTotalPagesEmpresa] = useState(1);
  const [totalEmpresas, setTotalEmpresas] = useState(0);
  
  // Negocios state
  const [deals, setDeals] = useState([]);
  const [dealsStats, setDealsStats] = useState({});
  const [loadingDeals, setLoadingDeals] = useState(true);
  const [searchDeal, setSearchDeal] = useState('');
  const [pageDeal, setPageDeal] = useState(1);
  const [totalPagesDeal, setTotalPagesDeal] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Edit Deal Modal state
  const [showEditDealModal, setShowEditDealModal] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [availableServices, setAvailableServices] = useState([]);
  
  // Modal state
  const [showNewModal, setShowNewModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [countryCode, setCountryCode] = useState('+57'); // Default Colombia
  const [formData, setFormData] = useState({
    name: '',
    documentType: 'NIT',
    documentNumber: '',
    dv: '', // Dígito de verificación
    personType: 'Persona Jurídica',
    email: '',
    phone: '',
    address: '',
  });

  const settings = state.settings || {};
  const currency = settings.currency || 'COP';

  // Calculate period dates - MUST match backend calculation exactly
  const getPeriodDates = () => {
    const now = new Date();
    let startDate;
    
    switch (period) {
      case 'week':
        // Start from Monday of current week (matches backend)
        const dayOfWeek = now.getDay();
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        startDate = new Date(now);
        startDate.setDate(now.getDate() - daysToMonday);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'semester':
        const semester = now.getMonth() < 6 ? 0 : 6;
        startDate = new Date(now.getFullYear(), semester, 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'all':
        // Return null for all time - no filtering
        return { startDate: null, endDate: null };
      case 'custom':
        // Use custom dates
        return {
          startDate: customDateFrom ? new Date(customDateFrom) : null,
          endDate: customDateTo ? new Date(customDateTo + 'T23:59:59') : now
        };
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        startDate.setHours(0, 0, 0, 0);
    }
    
    return { startDate, endDate: now };
  };

  const formatValue = (value) => {
    if (!value) return `$0 ${currency}`;
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M ${currency}`;
    }
    return `$${value.toLocaleString()} ${currency}`;
  };

  const loadEmpresas = useCallback(async () => {
    setLoadingEmpresas(true);
    try {
      // Get period dates for filtering
      const { startDate, endDate } = getPeriodDates();
      const data = await api.getEmpresas(
        pageEmpresa, 
        20, 
        searchEmpresa || null, 
        null,
        startDate ? startDate.toISOString() : null,
        endDate ? endDate.toISOString() : null
      );
      setEmpresas(data.empresas || []);
      setTotalPagesEmpresa(data.pages || 1);
      setTotalEmpresas(data.total || 0);
    } catch (error) {
      toast.error('Error al cargar empresas');
    } finally {
      setLoadingEmpresas(false);
    }
  }, [pageEmpresa, searchEmpresa, period, customDateFrom, customDateTo]);

  const loadDeals = useCallback(async () => {
    setLoadingDeals(true);
    try {
      // Get period dates
      const { startDate, endDate } = getPeriodDates();
      const customFrom = period === 'custom' ? customDateFrom : null;
      const customTo = period === 'custom' ? customDateTo : null;
      
      // Get deals stats from backend (consistent with Statistics page)
      const periodStats = await api.getDealsStatsByPeriod(period, customFrom, customTo);
      const monthlyStats = await api.getDealsMonthlyStats(period, customFrom, customTo, null);
      
      // Calculate won deals from monthly stats
      const wonDeals = monthlyStats.reduce((sum, m) => sum + m.count, 0);
      const wonValue = monthlyStats.reduce((sum, m) => sum + m.value, 0);
      
      // Set stats (consistent with Dashboard)
      const stats = {
        total: periodStats.total || 0,
        won: wonDeals,
        lost: periodStats.lost || 0,
        negotiation: periodStats.negotiation || 0,
        totalValue: periodStats.totalValue || 0,
        wonValue: wonValue,
      };
      
      setDealsStats(stats);
      
      // Load deals for list display (separate pagination)
      const data = await api.getDeals(pageDeal, 20, statusFilter === 'all' ? null : statusFilter, searchDeal || null);
      const allDeals = data.deals || [];
      
      // Filter by period for display
      const filteredDeals = allDeals.filter(deal => {
        if (!startDate && !endDate) return true;
        const relevantDate = deal.actualCloseDate ? new Date(deal.actualCloseDate) : new Date(deal.createdAt);
        if (startDate && relevantDate < startDate) return false;
        if (endDate && relevantDate > endDate) return false;
        return true;
      });
      
      setDeals(filteredDeals);
      setTotalPagesDeal(Math.ceil(filteredDeals.length / 20) || 1);
    } catch (error) {
      toast.error('Error al cargar negocios');
    } finally {
      setLoadingDeals(false);
    }
  }, [pageDeal, statusFilter, searchDeal, period, customDateFrom, customDateTo]);

  useEffect(() => {
    if (activeTab === 'empresas') {
      loadEmpresas();
    } else {
      loadDeals();
    }
  }, [activeTab, loadEmpresas, loadDeals, period, customDateFrom, customDateTo]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === 'empresas') {
        setPageEmpresa(1);
        loadEmpresas();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchEmpresa]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === 'negocios') {
        setPageDeal(1);
        loadDeals();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchDeal, statusFilter]);

  // Load available services for deal editing
  useEffect(() => {
    const loadServices = async () => {
      try {
        const services = await api.getServices();
        setAvailableServices(services || []);
      } catch (error) {
        console.error('Error loading services:', error);
      }
    };
    loadServices();
  }, []);

  // Edit deal functions
  const openEditDealModal = (deal) => {
    setEditingDeal({
      ...deal,
      services: deal.services || [],
    });
    setShowEditDealModal(true);
  };

  const handleUpdateDeal = async (e) => {
    e.preventDefault();
    if (!editingDeal) return;

    try {
      await api.updateDeal(editingDeal.id, {
        services: editingDeal.services,
        totalValue: editingDeal.services.reduce((acc, s) => acc + ((s.value || s.minPrice || 0) * (s.quantity || 1)), 0),
        estimatedCloseDate: editingDeal.estimatedCloseDate,
        notes: editingDeal.notes,
        status: editingDeal.status,
      });
      toast.success('Negocio actualizado exitosamente');
      setShowEditDealModal(false);
      setEditingDeal(null);
      loadDeals();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Error al actualizar negocio');
    }
  };

  const addServiceToEditingDeal = (service) => {
    setEditingDeal(prev => ({
      ...prev,
      services: [...prev.services, { ...service, value: service.value || service.minPrice || 0, quantity: 1 }]
    }));
  };

  const updateEditingDealServiceQuantity = (index, quantity) => {
    setEditingDeal(prev => ({
      ...prev,
      services: prev.services.map((s, i) => i === index ? { ...s, quantity: parseInt(quantity) || 1 } : s)
    }));
  };

  const removeServiceFromEditingDeal = (index) => {
    setEditingDeal(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }));
  };

  // Helper functions for currency formatting
  const formatNumberAsCurrency = (value) => {
    if (!value && value !== 0) return '';
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d]/g, '')) : value;
    if (isNaN(numValue)) return '';
    return new Intl.NumberFormat('es-CO').format(numValue);
  };

  const parseCurrencyToNumber = (value) => {
    if (!value) return 0;
    const cleanValue = value.toString().replace(/[^\d]/g, '');
    return parseInt(cleanValue) || 0;
  };

  const updateEditingDealServiceValue = (index, value) => {
    const numericValue = parseCurrencyToNumber(value);
    setEditingDeal(prev => ({
      ...prev,
      services: prev.services.map((s, i) => i === index ? { ...s, value: numericValue } : s)
    }));
  };

  const handleCreateEmpresa = async (e) => {
    e.preventDefault();
    
    // Validaciones de campos obligatorios
    if (!formData.name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    if (!formData.documentType) {
      toast.error('El tipo de documento es obligatorio');
      return;
    }
    if (!formData.documentNumber.trim()) {
      toast.error('El número de documento es obligatorio');
      return;
    }
    if (!formData.personType) {
      toast.error('El tipo de persona es obligatorio');
      return;
    }
    
    try {
      // Combinar documentNumber con DV si es NIT
      // Combinar código de país con teléfono si existe
      const dataToSend = {
        ...formData,
        documentNumber: formData.documentType === 'NIT' && formData.dv 
          ? `${formData.documentNumber}-${formData.dv}`
          : formData.documentNumber,
        phone: formData.phone ? `${countryCode} ${formData.phone}` : ''
      };
      delete dataToSend.dv; // No enviar el campo DV separado
      
      await api.createEmpresa(dataToSend);
      toast.success('Empresa creada exitosamente');
      setShowNewModal(false);
      setCountryCode('+57'); // Reset country code
      setFormData({
        name: '',
        documentType: 'NIT',
        documentNumber: '',
        dv: '',
        personType: 'Persona Jurídica',
        email: '',
        phone: '',
        address: '',
      });
      loadEmpresas();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Error al crear empresa');
    }
  };

  const handleDocTypeChange = (docType) => {
    setFormData(prev => ({
      ...prev,
      documentType: docType,
      dv: '', // Limpiar DV al cambiar tipo de documento
      personType: docType === 'NIT' ? 'Persona Jurídica' : 'Persona Natural'
    }));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      won: { label: 'Ganado', bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      lost: { label: 'Perdido', bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
      negotiation: { label: 'En Negociación', bg: 'bg-blue-100', text: 'text-blue-700', icon: TrendingUp },
      proposal: { label: 'Propuesta', bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
    };
    const config = statusConfig[status] || statusConfig.negotiation;
    const Icon = config.icon;
    return (
      <span className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${config.bg} ${config.text}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Building2 className="w-7 h-7 text-emerald-600" />
              Gestión Comercial
            </h1>
            <p className="text-slate-500 mt-1">Empresas y negocios</p>
          </div>
          {activeTab === 'empresas' && (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setShowBatchModal(true)} data-testid="batch-empresas-btn">
                <Building2 className="w-4 h-4 mr-2" />
                Crear en Lote
              </Button>
              <Button onClick={() => setShowNewModal(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Empresa
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-6">
            <TabsTrigger value="empresas" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Empresas
            </TabsTrigger>
            <TabsTrigger value="negocios" className="flex items-center gap-2">
              <Briefcase className="w-4 h-4" />
              Negocios
            </TabsTrigger>
          </TabsList>

          {/* Empresas Tab */}
          <TabsContent value="empresas" className="mt-0">
            {/* Period Filter and Search */}
            <div className="mb-6 flex flex-wrap items-center gap-4">
              <span className="text-sm font-medium text-slate-600">Periodo:</span>
              <div className="relative">
                <select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  className="appearance-none bg-white border border-slate-200 rounded-lg px-4 py-2 pr-10 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer"
                >
                  {PERIOD_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
              
              {/* Custom Date Range */}
              {period === 'custom' && (
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <input
                    type="date"
                    value={customDateFrom}
                    onChange={(e) => setCustomDateFrom(e.target.value)}
                    className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Desde"
                  />
                  <span className="text-slate-400">-</span>
                  <input
                    type="date"
                    value={customDateTo}
                    onChange={(e) => setCustomDateTo(e.target.value)}
                    className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Hasta"
                  />
                </div>
              )}
              
              {/* Search */}
              <div className="relative flex-1 max-w-md ml-auto">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por nombre, NIT o email..."
                  value={searchEmpresa}
                  onChange={(e) => setSearchEmpresa(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Stats Summary */}
            <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Building2 className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{totalEmpresas}</p>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      Total Empresas
                      <InfoTooltip content="Número total de empresas registradas en el sistema. Incluye todas las empresas sin importar su estado o actividad." />
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Briefcase className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">
                      {empresas.reduce((acc, e) => acc + (e.wonDeals || 0), 0)}
                    </p>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      Negocios Ganados
                      <InfoTooltip content="Suma de negocios con status 'Ganado' de las empresas actualmente visibles en la lista. Para ver el total real, consulte la pestaña Negocios." />
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">
                      {empresas.reduce((acc, e) => acc + (e.contactsCount || 0), 0)}
                    </p>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      Leads
                      <InfoTooltip content="Suma de contactos (leads) asociados a las empresas actualmente visibles. Cada contacto es un lead vinculado a una empresa." />
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald-600">
                      {formatValue(empresas.reduce((acc, e) => acc + (e.totalValue || 0), 0))}
                    </p>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      Valor Total
                      <InfoTooltip content="Suma del valor de todos los negocios de las empresas visibles. Incluye negocios en cualquier estado (ganados, perdidos, en negociación)." />
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Empresas Table */}
            {loadingEmpresas ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full mx-auto"></div>
                <p className="text-slate-500 mt-2">Cargando empresas...</p>
              </div>
            ) : empresas.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No hay empresas registradas</p>
                <Button onClick={() => setShowNewModal(true)} variant="outline" className="mt-4">
                  <Plus className="w-4 h-4 mr-2" />
                  Crear primera empresa
                </Button>
              </div>
            ) : (
              <>
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Empresa</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Documento</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Tipo</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Contacto</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Leads</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Negocios</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Ganados</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Valor Total</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {empresas.map((empresa) => (
                          <tr
                            key={empresa.id}
                            className="hover:bg-slate-50 transition-colors cursor-pointer"
                            onClick={() => navigate(`/empresa/${empresa.id}`)}
                            data-testid={`empresa-row-${empresa.id}`}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                                  {empresa.name?.charAt(0) || 'E'}
                                </div>
                                <div className="min-w-0">
                                  <p className="font-medium text-slate-900 truncate">{empresa.name}</p>
                                  {empresa.code && (
                                    <p className="text-xs text-slate-500 font-mono">{empresa.code}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm text-slate-600">{empresa.documentType}: {empresa.documentNumber}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                empresa.personType === 'Persona Jurídica' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                              }`}>
                                {empresa.personType === 'Persona Jurídica' ? 'Jurídica' : 'Natural'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm">
                                {empresa.email && (
                                  <p className="text-slate-600 truncate max-w-[180px]">{empresa.email}</p>
                                )}
                                {empresa.phone && (
                                  <p className="text-slate-500 text-xs">{empresa.phone}</p>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-700 rounded-full font-semibold text-sm">
                                {empresa.contactsCount || 0}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="inline-flex items-center justify-center w-8 h-8 bg-slate-100 text-slate-700 rounded-full font-semibold text-sm">
                                {empresa.totalDeals || 0}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="inline-flex items-center justify-center w-8 h-8 bg-green-100 text-green-700 rounded-full font-semibold text-sm">
                                {empresa.wonDeals || 0}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="font-semibold text-emerald-600">{formatValue(empresa.totalValue || 0)}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/empresa/${empresa.id}`)}
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                Ver
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {totalPagesEmpresa >= 1 && (
                  <div className="flex items-center justify-between mt-4 px-2">
                    <span className="text-sm text-slate-600">
                      {totalEmpresas > 0 
                        ? `Mostrando ${((pageEmpresa - 1) * 20) + 1} - ${Math.min(pageEmpresa * 20, totalEmpresas)} de ${totalEmpresas} empresas`
                        : 'No hay empresas'
                      }
                    </span>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setPageEmpresa(p => Math.max(1, p - 1))} disabled={pageEmpresa === 1}>
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm text-slate-600 px-2">Página {pageEmpresa} de {totalPagesEmpresa}</span>
                      <Button variant="outline" size="sm" onClick={() => setPageEmpresa(p => Math.min(totalPagesEmpresa, p + 1))} disabled={pageEmpresa === totalPagesEmpresa}>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* Negocios Tab */}
          <TabsContent value="negocios" className="mt-0">
            {/* Period Filter */}
            <div className="mb-6 flex flex-wrap items-center gap-4">
              <span className="text-sm font-medium text-slate-600">Periodo:</span>
              <div className="relative">
                <select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  className="appearance-none bg-white border border-slate-200 rounded-lg px-4 py-2 pr-10 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer"
                >
                  {PERIOD_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
              
              {/* Custom Date Range */}
              {period === 'custom' && (
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <input
                    type="date"
                    value={customDateFrom}
                    onChange={(e) => setCustomDateFrom(e.target.value)}
                    className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Desde"
                  />
                  <span className="text-slate-400">-</span>
                  <input
                    type="date"
                    value={customDateTo}
                    onChange={(e) => setCustomDateTo(e.target.value)}
                    className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Hasta"
                  />
                </div>
              )}
            </div>

            {/* Stats Summary */}
            <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Briefcase className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{dealsStats.total || 0}</p>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      Total Negocios
                      <InfoTooltip content="Número de negocios cargados. Esta es una muestra limitada de los registros recientes." />
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{dealsStats.won || 0}</p>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      Ganados
                      <InfoTooltip content="Negocios cerrados exitosamente en el período seleccionado. Se calcula por la fecha de cierre real (actualCloseDate), consistente con Dashboard y Statistics." />
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <PieChart className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-purple-600">{dealsStats.firstDeals || 0}</p>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      1er Negocio
                      <InfoTooltip content="Negocios marcados como 'Primer Negocio' con la empresa. Indica nuevos clientes adquiridos." />
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald-600">{formatValue(dealsStats.wonValue || 0)}</p>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      Valor Ganado
                      <InfoTooltip content="Suma del valor (totalValue) de todos los negocios ganados en el período. Consistente con Dashboard y Statistics." />
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Search and Filters */}
            <div className="mb-6 flex flex-col md:flex-row gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por empresa o servicio..."
                  value={searchDeal}
                  onChange={(e) => setSearchDeal(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
              >
                <option value="all">Todos los estados</option>
                <option value="negotiation">En Negociación</option>
                <option value="proposal">Propuesta</option>
                <option value="won">Ganados</option>
                <option value="lost">Perdidos</option>
              </select>
            </div>

            {/* Deals List */}
            {loadingDeals ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full mx-auto"></div>
                <p className="text-slate-500 mt-2">Cargando negocios...</p>
              </div>
            ) : deals.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No hay negocios en este periodo</p>
                <p className="text-sm text-slate-400 mt-1">Cambia el filtro de periodo o crea negocios desde las empresas</p>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {deals.map((deal) => (
                    <div
                      key={deal.id}
                      className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            {deal.code && (
                              <span className="text-xs font-mono text-indigo-600 bg-indigo-50 px-2 py-1 rounded font-semibold">{deal.code}</span>
                            )}
                            <button
                              onClick={() => deal.empresaId && navigate(`/empresa/${deal.empresaId}`)}
                              className="font-semibold text-emerald-600 hover:text-emerald-800 hover:underline"
                            >
                              {deal.empresaName || 'Sin empresa'}
                            </button>
                            {getStatusBadge(deal.status)}
                            {/* First Deal / Recurring Badge */}
                            {deal.isFirstDeal ? (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
                                1er Negocio
                              </span>
                            ) : (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-cyan-100 text-cyan-700">
                                Recurrente #{deal.dealNumber}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 mb-2">
                            Contacto: {deal.leadName || '-'}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {deal.services?.slice(0, 3).map((s, idx) => (
                              <span key={idx} className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
                                {s.name}
                              </span>
                            ))}
                            {deal.services?.length > 3 && (
                              <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
                                +{deal.services.length - 3} más
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right flex flex-col items-end gap-2">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditDealModal(deal);
                              }}
                              className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                              title="Editar negocio"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <p className="text-xl font-bold text-emerald-600">{formatValue(deal.totalValue)}</p>
                          </div>
                          {deal.estimatedCloseDate && (
                            <p className="text-xs text-slate-500 flex items-center gap-1 justify-end">
                              <Calendar className="w-3 h-3" />
                              {new Date(deal.estimatedCloseDate).toLocaleDateString('es-ES')}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {totalPagesDeal > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-6">
                    <Button variant="outline" size="sm" onClick={() => setPageDeal(p => Math.max(1, p - 1))} disabled={pageDeal === 1}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-slate-600">Página {pageDeal} de {totalPagesDeal}</span>
                    <Button variant="outline" size="sm" onClick={() => setPageDeal(p => Math.min(totalPagesDeal, p + 1))} disabled={pageDeal === totalPagesDeal}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* New Empresa Modal */}
      <Dialog open={showNewModal} onOpenChange={setShowNewModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-600" />
              Nueva Empresa
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateEmpresa} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Nombre de la Empresa <span className="text-red-500">*</span>
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Nombre de la empresa"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Tipo Documento <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.documentType}
                  onChange={(e) => handleDocTypeChange(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                >
                  {DOCUMENT_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Número Documento <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <Input
                    value={formData.documentNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, documentNumber: e.target.value }))}
                    placeholder={formData.documentType === 'NIT' ? '9 dígitos' : '8-10 dígitos'}
                    required
                    className="flex-1"
                  />
                  {formData.documentType === 'NIT' && (
                    <>
                      <span className="flex items-center text-slate-400 font-bold">-</span>
                      <Input
                        value={formData.dv}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 1);
                          setFormData(prev => ({ ...prev, dv: value }));
                        }}
                        placeholder="DV"
                        maxLength={1}
                        className="w-16 text-center"
                        title="Dígito de Verificación"
                      />
                    </>
                  )}
                </div>
                {formData.documentType === 'NIT' && (
                  <p className="text-xs text-slate-500 mt-1">El DV (Dígito de Verificación) es el último número después del guion</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Tipo de Persona <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.personType}
                onChange={(e) => setFormData(prev => ({ ...prev, personType: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                disabled
              >
                {PERSON_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">Asignado automáticamente según el tipo de documento</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email (opcional)</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@empresa.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Teléfono (opcional)</label>
                <div className="flex gap-2">
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="w-24 px-2 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 text-sm"
                  >
                    {COUNTRY_CODES.map(({ code, country, flag }) => (
                      <option key={code} value={code}>
                        {flag} {code}
                      </option>
                    ))}
                  </select>
                  <Input
                    value={formData.phone}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^\d\s]/g, '');
                      setFormData(prev => ({ ...prev, phone: value }));
                    }}
                    placeholder="300 000 0000"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Dirección (opcional)</label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Dirección de la empresa"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowNewModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                Crear Empresa
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Deal Modal */}
      <Dialog open={showEditDealModal} onOpenChange={setShowEditDealModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-emerald-600" />
              Editar Negocio
            </DialogTitle>
          </DialogHeader>
          
          {editingDeal && (
            <form onSubmit={handleUpdateDeal} className="space-y-4">
              {/* Lead info (read-only) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Lead Responsable
                </label>
                <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-700">
                  {editingDeal.leadName || editingDeal.contactName || 'Sin asignar'}
                </div>
              </div>

              {/* Services */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Servicios <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {availableServices.filter(s => !editingDeal.services?.find(ds => ds.id === s.id)).map(service => (
                    <button
                      key={service.id}
                      type="button"
                      onClick={() => addServiceToEditingDeal(service)}
                      className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-full transition-colors"
                    >
                      + {service.name}
                    </button>
                  ))}
                </div>
                
                {editingDeal.services?.length > 0 && (
                  <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                    {editingDeal.services.map((service, idx) => (
                      <div key={idx} className="bg-white p-3 rounded-lg border border-slate-200">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <span className="text-sm font-medium text-slate-900">{service.name}</span>
                          <button
                            type="button"
                            onClick={() => removeServiceFromEditingDeal(idx)}
                            className="text-red-500 hover:text-red-700 font-bold text-lg leading-none"
                          >
                            ×
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="block text-xs text-slate-500 mb-1">Cantidad</label>
                            <input
                              type="number"
                              min="1"
                              value={service.quantity}
                              onChange={(e) => updateEditingDealServiceQuantity(idx, e.target.value)}
                              className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-slate-500 mb-1">Valor unitario</label>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={formatNumberAsCurrency(service.value || service.minPrice || 0)}
                              onChange={(e) => updateEditingDealServiceValue(idx, e.target.value)}
                              className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm text-right"
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-slate-500 mb-1">Subtotal</label>
                            <div className="px-2 py-1.5 bg-emerald-50 border border-emerald-200 rounded text-sm text-right font-medium text-emerald-700">
                              {formatValue((service.value || service.minPrice || 0) * service.quantity)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="pt-3 border-t border-slate-300 flex justify-between items-center">
                      <span className="font-semibold text-slate-700">Total del Negocio:</span>
                      <span className="text-lg font-bold text-emerald-600">
                        {formatValue(editingDeal.services.reduce((acc, s) => acc + ((s.value || s.minPrice || 0) * s.quantity), 0))}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Fecha Estimada de Cierre</label>
                <Input
                  type="date"
                  value={editingDeal.estimatedCloseDate || ''}
                  onChange={(e) => setEditingDeal(prev => ({ ...prev, estimatedCloseDate: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notas</label>
                <textarea
                  value={editingDeal.notes || ''}
                  onChange={(e) => setEditingDeal(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                  rows={3}
                  placeholder="Notas adicionales..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowEditDealModal(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                  Guardar Cambios
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Batch Upload Modal */}
      <BatchUploadModal
        open={showBatchModal}
        onOpenChange={setShowBatchModal}
        title="Crear Empresas en Lote"
        description="Sube un archivo CSV o Excel con las columnas: nombre, tipo_documento (NIT/CC/CE), numero_documento, tipo_persona (opcional), email, telefono, direccion."
        templateColumns={['nombre', 'tipo_documento', 'numero_documento', 'tipo_persona', 'email', 'telefono', 'direccion']}
        templateFileName="plantilla_empresas.csv"
        onUpload={async (file) => {
          const result = await api.batchCreateEmpresas(file);
          if (result.created > 0) loadEmpresas();
          return result;
        }}
      />
    </div>
  );
}
